/**
 * OpenSeadragon counterpart of Slidemap.js.
 *
 * Above the stitched pyramid, camera fields (tiled levels) are painted onto one canvas over the OSD
 * canvas, positioned from `txty` exactly as OpenLayers does in Slidemap.updateLayers.
 */
import OpenSeadragon from 'openseadragon';
import BaseSlideMapOSD from './BaseSlideMapOSD';
import {getImageDimensions} from './utils/tileSourceUtils';
import {getVisibleTilesForExtent} from '../utils/map_utils';
import {FIELD_IMAGE_CACHE, ROTATE_OVERLAY_RETRY_MS} from './viewerConfig';

class SlidemapOSD extends BaseSlideMapOSD {
    _tiledOverlayRaf = null;
    _immediateZoomFinishRaf = null;
    _rotateOverlayRetryTimers = [];
    /** tiledZoom → Map<url, HTMLImageElement>; these images are never attached to the DOM. */
    _fieldImageCache = new Map();
    _tiledFieldsCanvas = null;
    _tiledFieldsCtx = null;

    componentDidMount() {
        super.componentDidMount();
        this.attachSlidemapOsdHandlers();
    }

    componentWillUnmount() {
        if (this._tiledOverlayRaf != null) cancelAnimationFrame(this._tiledOverlayRaf);
        if (this._immediateZoomFinishRaf != null) cancelAnimationFrame(this._immediateZoomFinishRaf);
        this.clearRotateOverlayRetries();
        this.detachSlidemapOsdHandlers();
        this.clearTiledOverlays();
        super.componentWillUnmount();
    }

    attachSlidemapOsdHandlers = () => {
        if (!this.viewer) return;
        this.viewer.addHandler('animation-finish', this.onZoomAnimationFinish);
        this.viewer.addHandler('zoom', this.onViewerImmediateZoom);
        this.viewer.addHandler('pan', this.scheduleTiledOverlayUpdate);
        this.viewer.addHandler('resize', this.scheduleTiledOverlayUpdate);
        this.viewer.addHandler('animation', this.onAnimationFrame);
        this.viewer.addHandler('rotate', this.scheduleTiledOverlayAfterRotate);
    };

    detachSlidemapOsdHandlers = () => {
        if (!this.viewer) return;
        this.viewer.removeHandler('animation-finish', this.onZoomAnimationFinish);
        this.viewer.removeHandler('zoom', this.onViewerImmediateZoom);
        this.viewer.removeHandler('pan', this.scheduleTiledOverlayUpdate);
        this.viewer.removeHandler('resize', this.scheduleTiledOverlayUpdate);
        this.viewer.removeHandler('animation', this.onAnimationFrame);
        this.viewer.removeHandler('rotate', this.scheduleTiledOverlayAfterRotate);
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        super.componentDidUpdate(prevProps);
        if (prevProps.displaySetting?.rotation !== this.props.displaySetting?.rotation) {
            this.scheduleTiledOverlayAfterRotate();
        }
        if (prevProps.slide?.id !== this.props.slide?.id) {
            this.reloadSlide();
        } else if (prevProps.slide?.txty !== this.props.slide?.txty ||
            prevProps.osdViewerConfig?.organisation !== this.props.osdViewerConfig?.organisation) {
            this.scheduleTiledOverlayUpdate();
        }
    }

    onViewerOpen() {
        super.onViewerOpen();
        this.updateTiledOverlay();
    }

    /** Skip redraws while the zoom spring runs; repainting every frame makes field tiles crawl. */
    onAnimationFrame = () => {
        if (this.isPinchGestureActive() || !this.viewer?.viewport) return;
        const vp = this.viewer.viewport;
        if (Math.abs(vp.getZoom(true) - vp.getZoom(false)) > 1e-7) return;
        this.updateTiledOverlay();
    };

    /** zoomTo(..., true) may not raise animation-finish; run the same finish path. */
    onViewerImmediateZoom = (event) => {
        if (this.isPinchGestureActive() || !event?.immediately) return;
        if (this._immediateZoomFinishRaf != null) cancelAnimationFrame(this._immediateZoomFinishRaf);
        this._immediateZoomFinishRaf = requestAnimationFrame(() => {
            this._immediateZoomFinishRaf = null;
            this.onZoomAnimationFinish();
        });
    };

    onZoomAnimationFinish = () => {
        if (this.isPinchGestureActive()) return;
        this.updateMapState();
        this.updateScaleBar();
        this.updateTiledOverlay();
    };

    onPinchGestureEnd = () => {
        this.viewer?.forceRedraw();
        this.updateTiledOverlay();
        requestAnimationFrame(() => {
            if (this.viewer?.viewport) this.updateTiledOverlay();
        });
    };

    // ------------------------------------------------------------------ tiled-field canvas

    /** Recreated if the viewer was rebuilt. */
    ensureTiledFieldsCanvas = () => {
        if (!this.viewer?.container) return null;
        if (this._tiledFieldsCanvas && this._tiledFieldsCanvas.parentNode !== this.viewer.container) {
            this._tiledFieldsCanvas = null;
            this._tiledFieldsCtx = null;
        }
        if (!this._tiledFieldsCanvas) {
            const canvas = document.createElement('canvas');
            canvas.className = 'morphle-tiled-fields-canvas';
            canvas.setAttribute('aria-hidden', 'true');
            this.viewer.container.appendChild(canvas);
            this._tiledFieldsCanvas = canvas;
            this._tiledFieldsCtx = canvas.getContext('2d', {alpha: true});
        }
        return this._tiledFieldsCanvas;
    };

    /** Resizes the backing store to the container and clears it; returns the device pixel ratio. */
    prepareTiledFieldsCanvas = () => {
        const canvas = this._tiledFieldsCanvas;
        const ctx = this._tiledFieldsCtx;
        if (!canvas || !ctx || !this.viewer?.container) return null;
        const cw = this.viewer.container.clientWidth;
        const ch = this.viewer.container.clientHeight;
        if (cw <= 0 || ch <= 0) return null;
        const dpr = window.devicePixelRatio || 1;
        const bw = Math.max(1, Math.floor(cw * dpr));
        const bh = Math.max(1, Math.floor(ch * dpr));
        if (canvas.width !== bw || canvas.height !== bh) {
            canvas.width = bw;
            canvas.height = bh;
            canvas.style.width = `${cw}px`;
            canvas.style.height = `${ch}px`;
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, bw, bh);
        return dpr;
    };

    hideTiledOverlays = () => {
        if (!this._tiledFieldsCanvas) return;
        this.prepareTiledFieldsCanvas();
        this._tiledFieldsCanvas.style.display = 'none';
    };

    clearTiledOverlays = () => {
        if (this._tiledFieldsCanvas?.parentNode) {
            this._tiledFieldsCanvas.parentNode.removeChild(this._tiledFieldsCanvas);
        }
        this._tiledFieldsCanvas = null;
        this._tiledFieldsCtx = null;
        this._fieldImageCache.clear();
    };

    scheduleTiledOverlayUpdate = () => {
        if (this.isPinchGestureActive() || this._tiledOverlayRaf != null) return;
        this._tiledOverlayRaf = requestAnimationFrame(() => {
            this._tiledOverlayRaf = null;
            this.updateTiledOverlay();
        });
    };

    clearRotateOverlayRetries = () => {
        this._rotateOverlayRetryTimers.forEach(id => clearTimeout(id));
        this._rotateOverlayRetryTimers = [];
    };

    /** Rotation settles over several frames and field images decode late, so redraw a few times. */
    scheduleTiledOverlayAfterRotate = () => {
        if (this.isPinchGestureActive()) return;
        this.viewer?.forceRedraw();
        this.clearRotateOverlayRetries();
        if (this._tiledOverlayRaf != null) {
            cancelAnimationFrame(this._tiledOverlayRaf);
            this._tiledOverlayRaf = null;
        }
        this.updateTiledOverlay();
        this.scheduleTiledOverlayUpdate();
        requestAnimationFrame(() => {
            this.updateTiledOverlay();
            requestAnimationFrame(() => this.updateTiledOverlay());
        });
        this._rotateOverlayRetryTimers = ROTATE_OVERLAY_RETRY_MS.map(ms => window.setTimeout(() => {
            if (this.viewer?.viewport) this.updateTiledOverlay();
        }, ms));
    };

    getVisibleFields = (tiledConfig) => {
        const extent = this.viewer.getView().calculateExtent(this.viewer.getSize());
        if (!extent || extent.length < 4) return null;
        return getVisibleTilesForExtent(extent, this.props.slide, tiledConfig.imageInfo, tiledConfig.lastTiledLevel);
    };

    fieldUrl = (tiledConfig, tiledZoom, key) => {
        const [posI, posJ] = key.split('|');
        return `${tiledConfig.tiledBaseUrl}/${tiledZoom}/x${posI}y${posJ}.${this.props.slide.img_type || 'jpg'}`;
    };

    /**
     * Paints the loaded field images for the current view, in OpenLayers display order. Positions use
     * the TiledImage's *current* springs so fields stay locked to the OSD canvas mid-animation.
     */
    redrawTiledFieldsCanvas = (tiledConfig, tiledZoom, fields) => {
        const ctx = this._tiledFieldsCtx;
        const currentMap = this._fieldImageCache.get(tiledZoom);
        const dpr = this.prepareTiledFieldsCanvas();
        if (!ctx || !currentMap || dpr == null) return;

        const {height} = getImageDimensions(this.props.slide);
        const {posList, mstCountMap} = fields;
        const keys = Object.keys(posList).sort((a, b) => (mstCountMap[a] || 0) - (mstCountMap[b] || 0));
        const vp = this.viewer.viewport;
        const tiledItem = this.viewer.world?.getItemCount() > 0 ? this.viewer.world.getItemAt(0) : null;
        const imageToViewerEl = (ix, iy) => tiledItem
            ? vp.pixelFromPoint(tiledItem.imageToViewportCoordinates(ix, iy, true), true)
            : vp.imageToViewerElementCoordinates(new OpenSeadragon.Point(ix, iy));

        // Rotated draws are not axis-aligned; nearest-neighbour would show jaggies on diagonals.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        for (const key of keys) {
            const img = currentMap.get(this.fieldUrl(tiledConfig, tiledZoom, key));
            if (!img || !img.complete || img.naturalWidth === 0) continue;

            const [xleft, yleft, xright, yright] = posList[key];
            const imageX = xleft;
            const imageY = height - yright;
            const imageW = xright - xleft;
            const imageH = yright - yleft;

            const p0 = imageToViewerEl(imageX, imageY);
            const p1 = imageToViewerEl(imageX + imageW, imageY);
            const p2 = imageToViewerEl(imageX, imageY + imageH);

            ctx.save();
            ctx.setTransform(
                dpr * (p1.x - p0.x) / imageW,
                dpr * (p1.y - p0.y) / imageW,
                dpr * (p2.x - p0.x) / imageH,
                dpr * (p2.y - p0.y) / imageH,
                dpr * p0.x,
                dpr * p0.y
            );
            ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, imageW, imageH);
            ctx.restore();
        }
    };

    updateTiledOverlay = () => {
        if (!this.viewer?.viewport || !this.props.slide) return;
        const tiledView = this.getTiledView();
        if (!tiledView) {
            this.hideTiledOverlays();
            return;
        }
        const {tiledConfig, tiledZoom} = tiledView;

        this.ensureTiledFieldsCanvas();
        if (this._tiledFieldsCanvas) this._tiledFieldsCanvas.style.display = 'block';

        const fields = this.getVisibleFields(tiledConfig);
        if (!fields) {
            // Bad extent (e.g. mid-spring after a rotate): clear rather than leave a stale composite.
            this.prepareTiledFieldsCanvas();
            this.scheduleTiledOverlayUpdate();
            return;
        }

        if (!this._fieldImageCache.has(tiledZoom)) this._fieldImageCache.set(tiledZoom, new Map());
        const currentMap = this._fieldImageCache.get(tiledZoom);
        const visibleUrls = new Set();
        for (const key in fields.posList) {
            const url = this.fieldUrl(tiledConfig, tiledZoom, key);
            visibleUrls.add(url);
            const cached = currentMap.get(url);
            if (cached) {
                // Re-insert so Map order is least recently shown first.
                currentMap.delete(url);
                currentMap.set(url, cached);
                continue;
            }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = this.scheduleTiledOverlayUpdate;
            img.onerror = this.scheduleTiledOverlayUpdate;
            img.src = url;
            currentMap.set(url, img);
        }
        this.pruneFieldImageCache(tiledZoom, visibleUrls);

        this.redrawTiledFieldsCanvas(tiledConfig, tiledZoom, fields);
    };

    /** Bounds memory over long sessions; unloaded images also stop downloading. */
    pruneFieldImageCache = (tiledZoom, visibleUrls) => {
        const release = (img) => {
            img.onload = null;
            img.onerror = null;
            img.src = '';
        };
        for (const [zoom, images] of this._fieldImageCache) {
            if (Math.abs(zoom - tiledZoom) > FIELD_IMAGE_CACHE.neighbourLevels) {
                images.forEach(release);
                this._fieldImageCache.delete(zoom);
            }
        }
        for (const images of this._fieldImageCache.values()) {
            let excess = images.size - FIELD_IMAGE_CACHE.maxImagesPerLevel;
            for (const [url, img] of images) {
                if (excess <= 0) break;
                if (visibleUrls.has(url)) continue;
                release(img);
                images.delete(url);
                excess -= 1;
            }
        }
    };

    reloadSlide = () => {
        if (!this.viewer || !this.props.slide) return;
        this.detachSlidemapOsdHandlers();
        this._detachNavigatorResizeHandle();
        this.clearTiledOverlays();
        this.destroyViewer();
        this.initViewer();
        this.attachSlidemapOsdHandlers();
    };
}

export default SlidemapOSD;
